import ListHeader from '@/common/components/ListHeader';
import TextField from '@/common/components/TextField';
import Table from '@/common/components/Table';
import useDebounce from '@/common/hooks/useDebounce';
import { useFilterParams } from '@/common/hooks/useFilterParams';
import { Mapper } from '@/common/services/mapper';
import { ErrorMessageWrapper } from '@/common/components/ErrorMessageWrapper';
import { Search, FileBox } from 'lucide-react';
import { useTmaisBanners } from '@/features/tmaisBanners/hooks/useTmaisBanners';
import { bannersDefaultColumns } from '@/features/tmaisBanners/constants/bannersDefaultColumns.constant';
import { TmaisBannerData } from '@/features/tmaisBanners/types/tmaisBanner.type';
import { TmaisBannersTransformer, TmaisBannerTableItem } from '@/features/tmaisBanners/services/tmaisBannersTransformer';
import useUserPermissions from '@/common/hooks/useUserPermissions';


const TmaisBannersList = () => {
  const { filterParams, setFilterParams } = useFilterParams();
  const { hasUserPermission, isUserPermissionsPending } = useUserPermissions();

  const hasFilterPermission = hasUserPermission('Admin::Tmais::Banners::Filter');
  const shouldEnableBannersQuery = !isUserPermissionsPending && hasFilterPermission;
  const canRenderBanners = isUserPermissionsPending || hasFilterPermission;

  const [search, setSearch] = useDebounce(filterParams.search || '');

  const {
    data: banners,
    meta,
    isPending,
    isLoading,
    error = {} as any,
    refetch,
  } = useTmaisBanners({
    filters: {
      search,
      page: filterParams.page || 1,
    },
    mapper: Mapper.mapWithTransformer<TmaisBannerData[], TmaisBannerTableItem[]>({
    transformer: TmaisBannersTransformer.tableTransformer(),
   }),
    enabled: shouldEnableBannersQuery,
  });

  const currentPage = Number(filterParams.page) || 1;

  return (
    <div className="flex flex-col gap-6">
      <ListHeader
        Icon={<FileBox className="w-10 h-10" strokeWidth={1.5} />}
        title="Anúncios e banners"
        createButtonText="Criar banner"
        operationKlassToCheckPermission='Admin::Tmais::Banners::Create'
        disableCreateButton={isPending || error?.status === 403}
      />

      <ErrorMessageWrapper
        className="h-[calc(100vh-128px)]"
        statusCode={canRenderBanners ? error?.status : 403}
        refetchFunction={refetch}
      >
        <div className="h-[calc(100vh-128px)] flex flex-col gap-6">
          <div className="flex flex-col gap-4">
            <TextField
              name="search"
              LeftIcon={<Search />}
              placeholder="Pesquisar"
              className="rounded-sm shadow-[4px_0px_16px_0px_rgba(0,_0,_0,_0.5)]"
              onChange={(event) => {
                const value = (event.target as HTMLInputElement).value;
                setSearch(value);
                setFilterParams((prev) => ({ ...prev, search: value, page: 1 }));
              }}
              value={filterParams.search || ''}
            />
          </div>

          <Table
            columns={bannersDefaultColumns}
            isLoadingData={isLoading}
            page={currentPage}
            rows={banners}
            totalPages={meta?.total_pages || 1}
            totalCount={meta?.total}
            hasPagination
            setPage={(page) => setFilterParams((prev) => ({ ...prev, page }))}
            rowEmptyValue="--"
          />
        </div>
      </ErrorMessageWrapper>
    </div>
  );
};

export default TmaisBannersList;
